import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../../hooks/useApiHelper";
import { IPendingPartCategory } from "../../models/PendingCategory/PendingCategory";
import { IDefaultResponse } from "../../models/DefaultResponse";
import { PendingCategoryQueryKeys } from "./keys";
import { CategoryQueryKeys } from "../Category/keys";

export default function usePendingCategorySingle(pendingCategoryId: string | undefined) {
  const { get, post, put } = useApiHelper();
  const queryClient = useQueryClient();

  const pendingCategory = useQuery({
    queryKey: PendingCategoryQueryKeys.single(pendingCategoryId),
    queryFn: () => get<IDefaultResponse<IPendingPartCategory>>(`/part/pending-categories/${pendingCategoryId}`).then(
      (res) => res.data,
    ),
    enabled: !!pendingCategoryId,
  });

  // Save edits to the pending category without promoting it.
  const update = useMutation({
    mutationFn: (category: IPendingPartCategory) => put(`/part/pending-categories/${category.id}`, category),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PendingCategoryQueryKeys.all() });
    },
  });

  // Confirm & add: promotes the (edited) pending category into the real part
  // categories and removes it from the pending list.
  const confirm = useMutation({
    mutationFn: (category: IPendingPartCategory) => post(`/part/pending-categories/${category.id}/confirm`, category),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PendingCategoryQueryKeys.all() });
      queryClient.invalidateQueries({ queryKey: CategoryQueryKeys.all() });
    },
  });

  return {
    pendingCategory,
    update,
    confirm,
  };
}
